import { Box, Button, Divider, MenuItem, Stack, TextField, TextFieldProps } from '@mui/material'
import { useTranslation } from 'react-i18next'
import useLifts from '../hooks/useLifts'
import { Project } from '../types'
import LiftCreateButton from './LiftCreateButton'
import { ChangeEvent } from 'react'

export interface LiftSelectProps extends Omit<TextFieldProps, 'value' | 'children'> {
  project?: Project
  value: number | null
  withLabel?: boolean
  withAddOption?: boolean
  withClearSelection?: boolean
}

export default function LiftSelect({
  project,
  withLabel = true,
  withAddOption = false,
  withClearSelection = false,
  name = 'lift',
  label,
  value,
  disabled,
  onChange,
  ...props
}: LiftSelectProps) {
  /**
   * The translate function.
   */
  const [t] = useTranslation('common')

  /**
   * The lifts.
   */
  const lifts = useLifts()

  /**
   * The lifts sorted by name.
   */
  const sortedLifts = lifts.toSorted((a, b) => a.name.localeCompare(b.name))

  /**
   * The translated label.
   */
  const translatedLabel = label ?? t('lift')

  return (
    <>
      <TextField
        select
        name={name}
        label={withLabel && translatedLabel}
        value={value ?? ''}
        disabled={disabled}
        SelectProps={{
          MenuProps: {
            keepMounted: withAddOption
          }
        }}
        {...props}
        onChange={onChange}
      >
        {sortedLifts.map((lift) => (
          <MenuItem key={lift.id} value={lift.id}>
            {lift.name}
          </MenuItem>
        ))}

        {sortedLifts.length > 0 && !disabled && withAddOption && <Divider />}

        {!disabled && withAddOption && (
          <Box sx={{ px: 4, pt: 1 }} onClick={(e) => e.stopPropagation()}>
            <LiftCreateButton project={project} variant="text" color="primary" fullWidth />
          </Box>
        )}
      </TextField>

      {value !== null && withClearSelection && (
        <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button
            type="button"
            variant="text"
            onClick={() => {
              onChange?.({
                target: {
                  name,
                  value: null
                }
              } as unknown as ChangeEvent<HTMLInputElement>)
            }}
          >
            {t('clearSelection')}
          </Button>
        </Stack>
      )}
    </>
  )
}
